import { Subject } from 'rxjs';
import * as _orderBy from 'lodash/orderBy';
import * as _filter from 'lodash/filter';
import { FormType } from './../../../enums/form.enum';
import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  AfterContentInit,
  ContentChildren,
  QueryList,
  OnChanges,
  SimpleChanges,
  OnDestroy
} from '@angular/core';
import {
  IDataGridDefinition,
  IDataGridSource,
  IDataGridEvent,
  IDataGridButtonEvent
} from '../../../interfaces/data-grid.interface';
import { SiuTemplateDirective } from '../../../directives/siu-template.directive';
import { AlertService } from '../../../services';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import {
  trigger,
  state,
  style,
  transition,
  animate
} from '@angular/animations';

enum GridType {
  serverPagination = 'server-pagination',
  list = 'list',
  localPagination = 'local-pagination'
}

class TableDataSource {
  pagination = {
    items: [],
    page: 1,
    pageSize: 5,
    total: 0,
    orderBy: null,
    orderDir: null
  };

  constructor(private source: any[], pagination = {}) {
    this.pagination = {
      ...this.pagination,
      ...pagination,
      items: this.source,
      total: this.source.length
    };
    this.paginate(this.source);
  }

  paginate = (source: any[]) => {
    const { page, pageSize, orderBy, orderDir } = this.pagination;
    this.pagination.total = source.length;
    if (orderBy && orderDir) {
      this.source = _orderBy(source, [orderBy], [orderDir]);
    }
    this.pagination = {
      ...this.pagination,
      items: source.slice((page - 1) * pageSize).slice(0, pageSize)
    };
  };

  setSource = source => {
    this.source = source;
  };

  setPagination = (pagination, textFilter = null) => {
    this.pagination = {
      ...this.pagination,
      ...pagination,
      total: this.source.length
    };
    if (textFilter) {
      this.paginate(this.searchTextInArray(this.source, textFilter));
    } else {
      this.paginate(this.source);
    }
  };

  searchTextInArray = (source, text) => {
    return source.filter(
      s =>
        Object.values(s).filter(val => {
          return `${val}`.toLowerCase().includes(text.toLowerCase());
        }).length > 0
    );
  };
}

@Component({
  selector: 'siu-data-grid',
  templateUrl: './data-grid-v2.component.html',
  styleUrls: ['./data-grid-v2.component.scss'],
  animations: [
    trigger('detailExpand', [
      state(
        'collapsed',
        style({ height: '0px', minHeight: '0', display: 'none' })
      ),
      state('expanded', style({ height: '*' }))
      // transition(
      //   'expanded <=> collapsed',
      //   animate('55ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      // )
    ])
  ]
})
export class DataGridV2Component
  implements OnInit, AfterContentInit, OnChanges, OnDestroy {
  @Input() emptyRowsMsg: string = 'No se encontraron resultados';
  @Input() loading: boolean;
  @Input() definition: IDataGridDefinition;
  @Input() source: IDataGridSource<any>;
  @Input() pageSizeOptions = [10, 25, 50, 100];
  @Output('on-load-data') onLoadDataEvent: EventEmitter<
    IDataGridEvent
  > = new EventEmitter();
  @Output('on-click-button') onClickButtonEvent: EventEmitter<
    IDataGridButtonEvent
  > = new EventEmitter();
  @Input() noElevation: boolean = false;
  @ContentChildren(SiuTemplateDirective) templates: QueryList<
    SiuTemplateDirective
  >;
  @Input() serverError?: any = null;
  @Output('on-close-new') onCloseNewEvent: EventEmitter<
    IDataGridButtonEvent
  > = new EventEmitter();
  @Input() gridType: GridType = GridType.serverPagination;
  @Input() showSearch: boolean = true;

  localFilter = new Subject<string>();

  GridTypeEnum = GridType;
  displayedColumns: string[];
  customTemplates: any = {};
  customHeadersTemplates: any = {};
  editableTemplate: any;
  expandableTemplate: any;

  /**
   * Variable que indica si el formulario de registro esta abierto.
   */
  @Input() openNew: boolean = false;
  isOpenNew: boolean = false;

  formType = FormType;

  /**
   * objeto que alamacena los datos para grilla editable por file
   * ejem: editWorkData[0]: alamacena los datos de edición para la fila 0
   */
  editWorkData: any = {};

  /**
   * Objeto sobre el cual se creará un nuevo registro
   */
  formNewModel: any = null;

  rowSelected: number = -1;

  localDataSource: TableDataSource;

  /**
   * objeto que alamacena los indices de las filas y su estado true: abierto, false: cerrado
   */
  expandableState: any = {};

  private unsubscribeAll: Subject<any> = new Subject();

  localTextFilter = '';

  constructor(private alertService: AlertService) {}

  ngOnInit() {
    this.buildLocalDataSource();
    this.subscribeToFilter();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.source &&
      changes.source.currentValue &&
      !changes.source.firstChange
    ) {
      this.buildLocalDataSource();
    }

    if (
      changes.openNew !== undefined &&
      changes.openNew.currentValue !== undefined
    ) {
      this.setOpenNew(changes.openNew.currentValue);
    }
  }

  ngAfterContentInit(): void {
    const customColumns = this.definition.columns.filter(x => x.template);

    if (customColumns.length > 0) {
      customColumns.forEach(x => {
        const matches = this.templates.filter(
          temp => temp.getType() === x.field
        );
        if (matches.length > 0) {
          this.customTemplates[x.field] = matches[0].template;
        }
      });
    }

    this.setHeadersTemplate();

    this.setEditableTemplate();

    this.setExpandableTemplate();

    const templateToolbarRight = this.templates.find(
      temp => temp.getType() === 'grid-toolbar-right'
    );
    if (templateToolbarRight) {
      this.customTemplates['grid-toolbar-right'] =
        templateToolbarRight.template;
    }
  }

  ngOnDestroy() {
    this.unsubscribeAll.next();
    this.unsubscribeAll.complete();
  }

  private buildLocalDataSource = () => {
    if (this.gridType !== GridType.serverPagination) {
      const defaultPagination = {} as any;

      if (this.gridType === GridType.list) {
        defaultPagination.pageSize = this.source.items.length;
      }

      this.localDataSource = new TableDataSource(
        this.source.items,
        defaultPagination
      );
    }
  };

  private setEditableTemplate = () => {
    const match = this.templates.find(
      temp => temp.getType() === 'row-editable'
    );
    if (match) {
      this.editableTemplate = match.template;
    }
  };

  private setHeadersTemplate = () => {
    this.definition.columns.forEach((e, i) => {
      if (e.headerTemplate) {
        const found = this.templates.find(
          temp => temp.getType() === e.headerTemplate
        );
        if (found) {
          this.customHeadersTemplates[i] = found.template;
        }
      }
    });
  };

  private setExpandableTemplate = () => {
    const match = this.templates.find(
      temp => temp.getType() === 'row-expandable'
    );
    if (match) {
      this.expandableTemplate = match.template;
    }
  };

  cloneObject = data => {
    return JSON.parse(JSON.stringify(data));
  };

  handleChangePage = page => {
    this.onLoadData(
      page,
      this.source.pageSize,
      this.source.orderBy,
      this.source.orderDir
    );
  };

  handleSort = event => {
    this.onLoadData(
      this.source.page,
      this.source.pageSize,
      event.active,
      event.direction
    );
  };

  handleChangePageSize = pageSize => {
    const page = 1;
    this.onLoadData(page, pageSize, this.source.orderBy, this.source.orderDir);
  };

  handleClickButton = e => {
    this.rowSelected = e.index;
    this.onClickButtonEvent.emit({
      item: e.item,
      action: e.action,
      index: e.index,
      events: {
        openEdit: () => {
          if (this.definition.editable) {
            this.handleEditRow(e.index);
          }
        }
      }
    });
  };

  handleEditRow = rowIndex => {
    this.checkBeforeEditInline().then(() => {
      this.rowSelected = rowIndex;
      const data = this.source.items[rowIndex];
      this.editWorkData[rowIndex] = {
        $$edit: true,
        data: this.cloneObject(data)
      };
    });
  };

  private calcSkipByPage = (page: number, pageSize: number): number => {
    return (page - 1) * pageSize;
  };

  private resetEditWorkData = () => {
    this.editWorkData = {};
  };

  handleCloseEditable = rowIndex => {
    return () => {
      this.editWorkData[rowIndex] = undefined;
    };
  };

  handleCloseNew = () => {
    this.onCloseNewEvent.emit();
    this.formNewModel = null;
  };

  private onLoadData = (page, pageSize, orderBy, orderDir) => {
    this.resetExpandableState();
    this.checkIsEditing()
      .then(() => {
        this.onLoadDataEvent.emit({
          page,
          pageSize,
          orderBy,
          orderDir,
          skip: this.calcSkipByPage(page, pageSize)
        });
      })
      .catch(() => {});
  };

  private isEditing = () => {
    return (
      Object.keys(this.editWorkData).filter(
        k => this.editWorkData[k] && this.editWorkData[k].$$edit
      ).length > 0
    );
  };

  // =============================================
  // Metodos pagination local
  // =============================================

  handleLocalChangePage = page => {
    this.checkIsEditing()
      .then(() => {
        this.localDataSource.setPagination(
          {
            page
          },
          this.localTextFilter
        );
      })
      .catch(() => {});
  };

  handleLocalChangePageSize = pageSize => {
    this.checkIsEditing()
      .then(() => {
        this.localDataSource.setPagination(
          {
            page: 1,
            pageSize
          },
          this.localTextFilter
        );
      })
      .catch(() => {});
  };

  handleLocalSort = event => {
    this.checkIsEditing()
      .then(() => {
        this.localDataSource.setPagination(
          {
            orderBy: event.active,
            orderDir: event.direction
          },
          this.localTextFilter
        );
      })
      .catch(() => {});
  };

  checkIsEditing = () => {
    return new Promise((resolve, reject) => {
      if (this.isEditing()) {
        this.alertService
          .open('Se perderán los datos que no hayan sido guardados', null, {
            confirm: true
          })
          .then(c => {
            if (c) {
              resolve();
              this.resetEditWorkData();
            }
          });
      } else {
        resolve();
        this.resetEditWorkData();
      }
    });
  };

  private checkBeforeEditInline = () => {
    return new Promise((resolve, reject) => {
      if (this.isOpenNew) {
        this.alertService
          .open('Se cancelará el formulario de registro', null, {
            confirm: true
          })
          .then(c => {
            if (c) {
              this.handleCloseNew();
              resolve();
            }
          });
      } else {
        this.handleCloseNew();
        resolve();
      }
    });
  };

  private subscribeToFilter = () => {
    this.localFilter
      .pipe(takeUntil(this.unsubscribeAll))
      .pipe(
        debounceTime(400),
        distinctUntilChanged()
      )
      .subscribe((e: any) => {
        this.localDataSource.setPagination({ page: 1 }, this.localTextFilter);
      });
  };

  private setOpenNew(open) {
    if (open) {
      setTimeout(() => {
        this.checkIsEditing()
          .then(() => {
            this.isOpenNew = open;
          })
          .catch(() => {
            this.handleCloseNew();
          });
      });
    } else {
      this.isOpenNew = open;
    }
  }

  get contextPaginator() {
    if (this.gridType === GridType.serverPagination) {
      return {
        $implicit: this.source,
        show: !this.serverError,
        disabled: this.loading,
        comboPageSizeOptions: this.pageSizeOptions,
        actions: {
          handleChangePage: this.handleChangePage,
          handleChangePageSize: this.handleChangePageSize
        }
      };
    } else {
      return {
        $implicit: this.localDataSource.pagination,
        show: this.gridType === GridType.localPagination,
        disabled: false,
        comboPageSizeOptions: [5, 10, 15, 20, 25],
        actions: {
          handleChangePage: this.handleLocalChangePage,
          handleChangePageSize: this.handleLocalChangePageSize
        }
      };
    }
  }

  get contextHeader() {
    if (this.gridType === GridType.serverPagination) {
      return {
        $implicit: this.source,
        actions: { handleSort: this.handleSort }
      };
    } else {
      return {
        $implicit: this.localDataSource.pagination,
        actions: { handleSort: this.handleLocalSort }
      };
    }
  }

  get columnsToDisplay() {
    return this.definition.columns.filter(x => !x.hidden);
  }

  rowClass = (rowIndex, item) => {
    const _class: any = {
      'row-selected': this.rowSelected === rowIndex,
      'row-background': rowIndex % 2 === 0
    };
    if (this.definition.rowClass) {
      Object.keys(this.definition.rowClass).forEach(k => {
        _class[k] = this.definition.rowClass[k](item);
      });
    }
    return _class;
  };

  handleOpenExpandable = index => () => {
    this.expandableState[index] = true;
  };

  handleCloseExpandable = index => () => {
    this.expandableState[index] = false;
  };

  resetExpandableState = () => {
    this.expandableState = {};
  };

  isRowExpanded = rowIndex => {
    // return true;
    return (
      this.expandableState[rowIndex] && this.expandableState[rowIndex] === true
    );
  };
}
